import { useRef } from 'react';
import { validarArquivo } from '@/utils/validation';
import { useToast } from '@/hooks/useToast';
import ToastContainer from '@/components/base/ToastContainer';

type DocStatus = 'pendente' | 'aprovado' | 'rejeitado';

interface DocLado {
  frente: string | null;
  verso: string | null;
  status: DocStatus;
  dataEnvio: string | null;
  motivoRejeicao: string | null;
}

interface DocSimples {
  arquivo: string | null;
  status: DocStatus;
  dataEnvio: string | null;
  motivoRejeicao: string | null;
}

interface Documentos {
  cpf: DocLado;
  rg: DocLado;
  cnh: DocLado;
  comprovante: DocSimples;
}

interface Props {
  tipoDocumento: 'cpf-rg' | 'cnh';
  setTipoDocumento: (v: 'cpf-rg' | 'cnh') => void;
  documentos: Documentos;
  setDocumentos: React.Dispatch<React.SetStateAction<Documentos>>;
}

const statusConfig: Record<DocStatus, { label: string; cls: string; icon: string }> = {
  pendente: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-700', icon: 'ri-time-line' },
  aprovado: { label: 'Aprovado', cls: 'bg-green-100 text-green-700', icon: 'ri-check-line' },
  rejeitado: { label: 'Rejeitado', cls: 'bg-red-100 text-red-700', icon: 'ri-close-line' },
};

function StatusBadge({ status }: { status: DocStatus }) {
  const cfg = statusConfig[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
      <i className={cfg.icon}></i>
      {cfg.label}
    </span>
  );
}

interface UploadCardProps {
  titulo: string;
  subtitulo: string;
  icon: string;
  imagemUrl: string | null;
  status: DocStatus;
  dataEnvio: string | null;
  motivoRejeicao: string | null;
  onUpload: (file: File) => void;
  onRemover: () => void;
}

function UploadCard({ titulo, subtitulo, icon, imagemUrl, status, dataEnvio, motivoRejeicao, onUpload, onRemover }: UploadCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const validacao = validarArquivo(file, tiposPermitidos, 5);
    if (!validacao.valido) {
      toast.error(validacao.erro || 'Arquivo inválido', 'Use JPG, PNG ou PDF com até 5MB.');
      return;
    }
    onUpload(file);
    // reset input
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      <div className={`rounded-xl border-2 overflow-hidden transition-all ${
        imagemUrl
          ? status === 'aprovado' ? 'border-green-300 bg-green-50/30'
          : status === 'rejeitado' ? 'border-red-300 bg-red-50/30'
          : 'border-orange-300 bg-orange-50/20'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              imagemUrl ? 'bg-orange-100' : 'bg-slate-100'
            }`}>
              <i className={`${icon} text-lg ${imagemUrl ? 'text-orange-600' : 'text-slate-400'}`}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">{titulo}</p>
              <p className="text-xs text-slate-500">{subtitulo}</p>
            </div>
          </div>
          {imagemUrl && <StatusBadge status={status} />}
        </div>

        {/* Body */}
        <div className="p-4">
          {imagemUrl ? (
            <div className="space-y-3">
              {/* Preview */}
              <div className="relative rounded-lg overflow-hidden bg-slate-100 border border-slate-200" style={{ height: 140 }}>
                <img
                  src={imagemUrl}
                  alt={titulo}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                {dataEnvio && (
                  <span className="absolute bottom-2 left-2 text-[10px] text-white bg-black/50 px-2 py-0.5 rounded-full">
                    Enviado em {new Date(dataEnvio).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>

              {/* Motivo rejeição */}
              {status === 'rejeitado' && motivoRejeicao && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-red-700">{motivoRejeicao}</p>
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-2">
                <button
                  onClick={() => inputRef.current?.click()}
                  className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Substituir
                </button>
                <button
                  onClick={onRemover}
                  className="px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                  Remover
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center gap-2 hover:border-orange-400 hover:bg-orange-50/30 transition-all cursor-pointer group"
            >
              <div className="w-12 h-12 bg-slate-100 group-hover:bg-orange-100 rounded-full flex items-center justify-center transition-colors">
                <i className="ri-upload-cloud-2-line text-2xl text-slate-400 group-hover:text-orange-500 transition-colors"></i>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700 group-hover:text-orange-700 transition-colors">Clique para enviar</p>
                <p className="text-xs text-slate-500 mt-0.5">JPG, PNG ou PDF · máx. 5MB</p>
              </div>
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,application/pdf"
        className="hidden"
        onChange={handleChange}
      />
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </>
  );
}

export default function DocumentosTab({ tipoDocumento, setTipoDocumento, documentos, setDocumentos }: Props) {
  const toast = useToast();

  const handleUpload = (
    tipo: 'cpf' | 'rg' | 'cnh' | 'comprovante',
    lado: 'frente' | 'verso' | 'arquivo',
    file: File
  ) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setDocumentos(prev => {
        const next = { ...prev };
        if (tipo === 'comprovante') {
          next.comprovante = { arquivo: result, status: 'pendente', dataEnvio: new Date().toISOString(), motivoRejeicao: null };
        } else {
          next[tipo] = { ...next[tipo], [lado]: result, status: 'pendente', dataEnvio: new Date().toISOString(), motivoRejeicao: null };
        }
        return next;
      });
      toast.success('Documento enviado', 'O arquivo foi carregado e está aguardando análise.');
    };
    reader.readAsDataURL(file);
  };

  const handleRemover = (tipo: 'cpf' | 'rg' | 'cnh' | 'comprovante', lado: 'frente' | 'verso' | 'arquivo') => {
    setDocumentos(prev => {
      const next = { ...prev };
      if (tipo === 'comprovante') {
        next.comprovante = { arquivo: null, status: 'pendente', dataEnvio: null, motivoRejeicao: null };
      } else {
        next[tipo] = { ...next[tipo], [lado]: null };
      }
      return next;
    });
    toast.info('Documento removido', 'O arquivo foi removido com sucesso.');
  };

  // Progresso geral
  const totalSlots = tipoDocumento === 'cpf-rg' ? 5 : 3; // cpf-frente, cpf-verso, rg-frente, rg-verso, comprovante | cnh-frente, cnh-verso, comprovante
  const enviados = tipoDocumento === 'cpf-rg'
    ? [documentos.cpf.frente, documentos.cpf.verso, documentos.rg.frente, documentos.rg.verso, documentos.comprovante.arquivo].filter(Boolean).length
    : [documentos.cnh.frente, documentos.cnh.verso, documentos.comprovante.arquivo].filter(Boolean).length;
  const progresso = Math.round((enviados / totalSlots) * 100);

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-1">Documentos de Identificação</h3>
        <p className="text-xs lg:text-sm text-slate-600">
          Envie seus documentos para validação da conta. Formatos aceitos: JPG, PNG ou PDF (máx. 5MB)
        </p>
      </div>

      {/* Progresso */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-700">Progresso do envio</p>
          <span className="text-xs font-bold text-orange-600">{enviados}/{totalSlots} documentos</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-orange-400 to-orange-600 rounded-full transition-all duration-500"
            style={{ width: `${progresso}%` }}
          ></div>
        </div>
        {progresso === 100 && (
          <p className="text-xs text-green-600 font-semibold mt-2 flex items-center gap-1">
            <i className="ri-check-double-line"></i>
            Todos os documentos enviados! Aguardando análise.
          </p>
        )}
      </div>

      {/* Seletor de Tipo */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
        <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-3 block">
          Tipo de documento de identificação:
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'cpf-rg' as const, label: 'CPF + RG', icon: 'ri-bank-card-line' },
            { id: 'cnh' as const, label: 'CNH', icon: 'ri-id-card-line' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setTipoDocumento(opt.id)}
              className={`px-4 py-4 rounded-lg border-2 transition-all font-semibold text-sm flex flex-col items-center gap-2 cursor-pointer ${
                tipoDocumento === opt.id
                  ? 'border-orange-500 bg-orange-50 text-orange-700'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
              }`}
            >
              <i className={`${opt.icon} text-2xl`}></i>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards de Upload — CPF + RG */}
      {tipoDocumento === 'cpf-rg' && (
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <i className="ri-bank-card-line text-orange-500"></i>
            CPF
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UploadCard
              titulo="CPF — Frente"
              subtitulo="Foto da frente do CPF"
              icon="ri-bank-card-line"
              imagemUrl={documentos.cpf.frente}
              status={documentos.cpf.status}
              dataEnvio={documentos.cpf.dataEnvio}
              motivoRejeicao={documentos.cpf.motivoRejeicao}
              onUpload={(f) => handleUpload('cpf', 'frente', f)}
              onRemover={() => handleRemover('cpf', 'frente')}
            />
            <UploadCard
              titulo="CPF — Verso"
              subtitulo="Foto do verso do CPF"
              icon="ri-bank-card-2-line"
              imagemUrl={documentos.cpf.verso}
              status={documentos.cpf.status}
              dataEnvio={documentos.cpf.dataEnvio}
              motivoRejeicao={documentos.cpf.motivoRejeicao}
              onUpload={(f) => handleUpload('cpf', 'verso', f)}
              onRemover={() => handleRemover('cpf', 'verso')}
            />
          </div>

          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2 pt-1">
            <i className="ri-contacts-line text-orange-500"></i>
            RG
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UploadCard
              titulo="RG — Frente"
              subtitulo="Foto da frente do RG"
              icon="ri-contacts-line"
              imagemUrl={documentos.rg.frente}
              status={documentos.rg.status}
              dataEnvio={documentos.rg.dataEnvio}
              motivoRejeicao={documentos.rg.motivoRejeicao}
              onUpload={(f) => handleUpload('rg', 'frente', f)}
              onRemover={() => handleRemover('rg', 'frente')}
            />
            <UploadCard
              titulo="RG — Verso"
              subtitulo="Foto do verso do RG"
              icon="ri-file-user-line"
              imagemUrl={documentos.rg.verso}
              status={documentos.rg.status}
              dataEnvio={documentos.rg.dataEnvio}
              motivoRejeicao={documentos.rg.motivoRejeicao}
              onUpload={(f) => handleUpload('rg', 'verso', f)}
              onRemover={() => handleRemover('rg', 'verso')}
            />
          </div>
        </div>
      )}

      {/* Cards de Upload — CNH */}
      {tipoDocumento === 'cnh' && (
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <i className="ri-id-card-line text-orange-500"></i>
            CNH
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UploadCard
              titulo="CNH — Frente"
              subtitulo="Foto da frente da CNH"
              icon="ri-id-card-line"
              imagemUrl={documentos.cnh.frente}
              status={documentos.cnh.status}
              dataEnvio={documentos.cnh.dataEnvio}
              motivoRejeicao={documentos.cnh.motivoRejeicao}
              onUpload={(f) => handleUpload('cnh', 'frente', f)}
              onRemover={() => handleRemover('cnh', 'frente')}
            />
            <UploadCard
              titulo="CNH — Verso"
              subtitulo="Foto do verso da CNH"
              icon="ri-id-card-line"
              imagemUrl={documentos.cnh.verso}
              status={documentos.cnh.status}
              dataEnvio={documentos.cnh.dataEnvio}
              motivoRejeicao={documentos.cnh.motivoRejeicao}
              onUpload={(f) => handleUpload('cnh', 'verso', f)}
              onRemover={() => handleRemover('cnh', 'verso')}
            />
          </div>
        </div>
      )}

      {/* Comprovante de Endereço — sempre visível */}
      <div className="space-y-3">
        <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <i className="ri-home-3-line text-orange-500"></i>
          Comprovante de Endereço
        </h4>
        <UploadCard
          titulo="Comprovante de Endereço"
          subtitulo="Conta de luz, água, telefone ou bancário (máx. 3 meses)"
          icon="ri-home-3-line"
          imagemUrl={documentos.comprovante.arquivo}
          status={documentos.comprovante.status}
          dataEnvio={documentos.comprovante.dataEnvio}
          motivoRejeicao={documentos.comprovante.motivoRejeicao}
          onUpload={(f) => handleUpload('comprovante', 'arquivo', f)}
          onRemover={() => handleRemover('comprovante', 'arquivo')}
        />
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex gap-3">
          <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5"></i>
          <div className="text-xs text-amber-800 space-y-1 min-w-0">
            <p className="font-semibold">Dicas para um envio aprovado:</p>
            <p>• Documentos devem estar legíveis, sem cortes ou reflexos</p>
            <p>• Não serão aceitas fotos de tela ou documentos editados</p>
            <p>• Comprovante de endereço com no máximo 3 meses de emissão</p>
            <p>• Use boa iluminação ao fotografar os documentos</p>
            <p>• A análise pode levar até 2 dias úteis</p>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
}
